import type { ArticleDto } from "@serophin/nottldr-shared";
import { ArrowRight, ChevronDown } from "lucide-preact";

/** The paragraphs of an article hold inline HTML (links, emphasis) as the generator wrote them. */
const Paragraphs = ({ items }: { items: string[] }) => (
  <>
    {items.map((paragraph) => (
      <p key={paragraph} dangerouslySetInnerHTML={{ __html: paragraph }} />
    ))}
  </>
);

type SummaryProps = { items: string[]; expanded: boolean; onToggle: () => void };

export const Summary = ({ items, expanded, onToggle }: SummaryProps) => (
  <div class="mb-4">
    <div class="bg-neutral-100 dark:bg-neutral-700 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        class="w-full p-3 flex items-center justify-between text-left hover:bg-neutral-200/50 dark:hover:bg-neutral-600/50 transition-colors"
      >
        <span class="text-sm font-medium text-gray-800 dark:text-gray-200">Summary</span>
        <ChevronDown class={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
      </button>
      <div class={`transition-all duration-200 ease-in-out ${expanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
        <div class="p-3 space-y-4">
          {items.map((item) => (
            <div key={item} class="flex items-start">
              <div class="flex-shrink-0">
                <ArrowRight class="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </div>
              <p class="ml-3 text-sm text-gray-800 dark:text-gray-200">{item}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const Section = ({ items }: { items: string[] }) => (
  <section class="mb-8">
    <div class="space-y-4 text-gray-700 dark:text-gray-300">
      <Paragraphs items={items} />
    </div>
  </section>
);

type KeyPoint = ArticleDto["keyPoints"][number];

const KeyPointSection = ({ keyPoint }: { keyPoint: KeyPoint }) => (
  <section class="mb-8">
    <h2 class="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">{keyPoint.title}</h2>
    <div class="space-y-4 text-gray-700 dark:text-gray-300">
      <Paragraphs items={keyPoint.paragraphs} />
      <blockquote class="mt-4 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border-l-4 border-blue-600 dark:border-blue-400 text-blue-900 dark:text-blue-100">
        <strong>Takeaway</strong> <p dangerouslySetInnerHTML={{ __html: keyPoint.takeaway }} />
      </blockquote>
    </div>
  </section>
);

/** The introduction, the key points with their takeaways, and the conclusion. */
export const ArticleBody = ({ article }: { article: ArticleDto }) => (
  <div class="article-body">
    <Section items={article.introduction} />
    {article.keyPoints.map((keyPoint) => (
      <KeyPointSection key={keyPoint.title} keyPoint={keyPoint} />
    ))}
    <Section items={article.conclusion} />
  </div>
);
